/**
 * Dashboard API Client
 *
 * Phase 3.5-A2: Dashboard Data Hooks
 */

import type { DashboardMetrics, PlatformHealthScore } from '../types';

const API_BASE = '/api/admin/dashboard';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Request failed (${response.status})`);
  }
  return response.json();
}

export const fetchDashboardMetrics = async (
  authFetch: AuthFetch
): Promise<DashboardMetrics> => {
  const response = await authFetch(`${API_BASE}/metrics`);
  return handleResponse<DashboardMetrics>(response);
};

export const fetchPlatformHealth = async (
  authFetch: AuthFetch
): Promise<PlatformHealthScore> => {
  const response = await authFetch(`${API_BASE}/health`);
  return handleResponse<PlatformHealthScore>(response);
};

// WQ-128: Operational health, tenant resources, security posture

export interface OperationalHealth {
  cert_expiry: { domain: string; days_until_expiry: number; status: string } | null;
  backup_recency: {
    status: string;
    tenants: Record<string, { hours_ago: number | null; status: string }>;
  };
  api_error_rate_percent: number | null;
  api_latency_p95_ms: number | null;
  container_count_running: number | null;
  container_count_total: number | null;
}

export interface TenantResource {
  tenant: string;
  cpu_percent: number;
  memory_mb: number;
  network_rx_mbps: number;
  container_count: number;
}

export interface SecurityPosture {
  waf: {
    configured: boolean;
    message?: string;
    blocked_24h?: number;
    allowed_24h?: number;
    block_rate_percent?: number;
    top_rules?: { rule: string; hits: number }[];
    rate_limited_24h?: number;
  };
  authentication: {
    failed_logins_24h?: number;
    active_sessions?: number;
    mfa_enabled_users?: number;
    total_users?: number;
    mfa_coverage_percent?: number;
    error?: string;
  };
  network: {
    configured: boolean;
    message?: string;
    sg_name?: string;
    inbound_rules?: number;
    ssh_open_to_world?: boolean;
    https_enforced?: boolean;
    cloudfront_only_origin?: boolean;
    warnings?: string[];
    warning_count?: number;
  };
  last_updated: string;
}

export const fetchOperationalHealth = async (
  authFetch: AuthFetch
): Promise<OperationalHealth> => {
  const response = await authFetch(`${API_BASE}/operational-health`);
  return handleResponse<OperationalHealth>(response);
};

export const fetchTenantResources = async (
  authFetch: AuthFetch
): Promise<{ tenants: TenantResource[] }> => {
  const response = await authFetch(`${API_BASE}/tenant-resources`);
  return handleResponse<{ tenants: TenantResource[] }>(response);
};

export const fetchSecurityPosture = async (
  authFetch: AuthFetch
): Promise<SecurityPosture> => {
  const response = await authFetch(`${API_BASE}/security-posture`);
  return handleResponse<SecurityPosture>(response);
};
